import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { AlertCircle, CheckCircle2, HelpCircle, X, XCircle } from "lucide-react";
import { bindAppPopup, setNativeAlert } from "../../utils/appPopup";

const AppPopupContext = createContext(null);
const PINK = "#d86a78";
const PINK_SOFT = "#f2c6cc";

export function useAppPopup() {
  const ctx = useContext(AppPopupContext);
  if (!ctx) {
    throw new Error("useAppPopup must be used inside AppPopupProvider");
  }
  return ctx;
}

export default function AppPopupProvider({ children }) {
  const [popup, setPopup] = useState(null);
  const [promptValue, setPromptValue] = useState("");
  const nativeAlertRef = useRef(null);
  const resolverRef = useRef(null);

  const closeWith = useCallback((value) => {
    const resolver = resolverRef.current;
    resolverRef.current = null;
    setPopup(null);
    setPromptValue("");
    if (resolver) resolver(value);
  }, []);

  const show = useCallback((type, message, options = {}) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setPromptValue(String(options?.defaultValue || ""));
      setPopup({
        type,
        message: String(message || ""),
        title: options?.title || defaultTitle(type),
        confirmText: options?.confirmText || (type === "alert" ? "OK" : "YES"),
        cancelText: options?.cancelText || "NO",
        placeholder: options?.placeholder || "Type here...",
        danger: !!options?.danger,
      });
    });
  }, []);

  const api = useMemo(() => ({
    alert: (message, options) => show("alert", message, options),
    confirm: (message, options) => show("confirm", message, options),
    prompt: (message, options) => show("prompt", message, options),
  }), [show]);

  useEffect(() => {
    bindAppPopup(api);
    if (!nativeAlertRef.current && typeof window !== "undefined") {
      nativeAlertRef.current = window.alert.bind(window);
      setNativeAlert(nativeAlertRef.current);
      window.alert = (message) => {
        api.alert(message);
      };
    }

    return () => {
      bindAppPopup(null);
      if (nativeAlertRef.current && typeof window !== "undefined") {
        window.alert = nativeAlertRef.current;
      }
    };
  }, [api]);

  useEffect(() => {
    if (!popup) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") closeWith(popup.type === "confirm" ? false : null);
      if (event.key === "Enter" && popup.type !== "prompt") {
        closeWith(popup.type === "confirm" ? true : undefined);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [popup, closeWith]);

  return (
    <AppPopupContext.Provider value={api}>
      {children}
      {popup ? (
        <div className="fixed inset-0 z-[100000]">
          <div
            className="absolute inset-0 bg-white/40 backdrop-blur-[3px]"
            onClick={() => closeWith(popup.type === "confirm" ? false : null)}
          />

          <div className="relative z-[1] flex min-h-full items-center justify-center p-4">
            <div
              className="relative w-[520px] max-w-[94vw] rounded-[18px] bg-white px-7 py-7 shadow-2xl"
              style={{ border: `1px solid ${PINK_SOFT}` }}
            >
              <button
                type="button"
                onClick={() => closeWith(popup.type === "confirm" ? false : null)}
                className="absolute right-5 top-4 flex h-8 w-8 items-center justify-center rounded-full text-slate-400 hover:bg-rose-50 hover:text-[#d86a78]"
                aria-label="Close popup"
              >
                <X size={18} />
              </button>

              <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-rose-50">
                {popup.type === "confirm" ? <HelpCircle size={34} color={PINK} /> : null}
                {popup.type === "prompt" ? <AlertCircle size={34} color={PINK} /> : null}
                {popup.type === "alert" && popup.danger ? <XCircle size={34} color={PINK} /> : null}
                {popup.type === "alert" && !popup.danger ? <CheckCircle2 size={34} color={PINK} /> : null}
              </div>

              <div className="text-center">
                <h3 className="text-[18px] font-semibold text-slate-800">{popup.title}</h3>
                {popup.message ? (
                  <p className="mx-auto mt-3 max-w-[420px] whitespace-pre-line text-[14px] leading-6 text-slate-500">
                    {popup.message}
                  </p>
                ) : null}
              </div>

              {popup.type === "prompt" ? (
                <textarea
                  value={promptValue}
                  onChange={(e) => setPromptValue(e.target.value)}
                  placeholder={popup.placeholder}
                  rows={4}
                  className="mt-5 w-full resize-none rounded-xl border border-rose-100 bg-rose-50/30 px-4 py-3 text-sm text-slate-700 outline-none focus:border-[#d86a78] focus:ring-2 focus:ring-rose-100"
                  autoFocus
                />
              ) : null}

              <div className="mt-7 flex flex-col-reverse items-center justify-center gap-3 sm:flex-row">
                {popup.type !== "alert" ? (
                  <button
                    type="button"
                    onClick={() => closeWith(popup.type === "confirm" ? false : null)}
                    className="h-10 w-full rounded-lg bg-slate-500 text-[13px] font-semibold text-white hover:bg-slate-600 sm:w-[140px]"
                  >
                    {popup.cancelText}
                  </button>
                ) : null}

                <button
                  type="button"
                  onClick={() => {
                    if (popup.type === "confirm") return closeWith(true);
                    if (popup.type === "prompt") return closeWith(promptValue);
                    return closeWith(undefined);
                  }}
                  className="h-10 w-full rounded-lg text-[13px] font-semibold text-white hover:opacity-90 sm:w-[140px]"
                  style={{ background: PINK }}
                >
                  {popup.confirmText}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </AppPopupContext.Provider>
  );
}

function defaultTitle(type) {
  if (type === "confirm") return "Please confirm";
  if (type === "prompt") return "Input required";
  return "Message";
}
